import { useState, useEffect, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api } from "@/App";
import { toast } from "sonner";
import {
  Settings,
  Mail,
  AlertTriangle,
  CheckCircle2,
  RefreshCw,
  Link2,
  Unlink,
  ArrowLeft,
  Lock,
  Shield
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

// Email Connect Modal Component - Gmail Only
function EmailConnectModal({ open, onClose, onSuccess }) {
  const [connected, setConnected] = useState(false);
  const [loading, setLoading] = useState(false);
  const [polling, setPolling] = useState(null);

  useEffect(() => {
    if (open) {
      fetchProviders();
    }
    return () => {
      if (polling) clearInterval(polling);
    };
  }, [open]);

  const fetchProviders = async () => {
    try {
      const res = await api.get("/crm/oauth/providers");
      if (res.data.items) {
        setConnected(!!res.data.items.find(x => x.provider === "google"));
      }
    } catch (e) {
      console.error("Failed to fetch providers:", e);
    }
  };

  const connect = async () => {
    setLoading(true);
    try {
      const res = await api.get("/crm/oauth/authorize?provider=google");
      if (res.data.auth_url) {
        // Open OAuth popup
        window.open(res.data.auth_url, "_blank", "width=520,height=680");
        
        // Poll for connection success
        let tries = 0;
        const interval = setInterval(async () => {
          tries++;
          try {
            const checkRes = await api.get("/crm/oauth/providers");
            const isConnected = !!checkRes.data.items?.find(x => x.provider === "google");
            if (isConnected) {
              clearInterval(interval);
              setPolling(null);
              setConnected(true);
              toast.success("Gmail connected successfully!");
              if (onSuccess) onSuccess();
            }
          } catch (e) {
            console.error("Poll error:", e);
          }
          if (tries > 60) {
            clearInterval(interval);
            setPolling(null);
          }
        }, 2000);
        setPolling(interval);
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to initiate OAuth");
    } finally {
      setLoading(false);
    }
  };

  if (!open) return null;

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="bg-slate-800 border-slate-700 max-w-md">
        <DialogHeader>
          <DialogTitle className="text-indigo-500 flex items-center gap-2">
            <Mail className="w-5 h-5 text-indigo-400" />
            Connect Your Email
          </DialogTitle>
          <DialogDescription className="text-indigo-400">
            Read-only access for CRM: sender, subject, timestamp. Your emails stay private.
          </DialogDescription>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          {/* Gmail Button */}
          <button
            disabled={connected || loading}
            onClick={connect}
            className={`w-full p-4 rounded-xl border transition-all flex items-center justify-between border-slate-700 bg-slate-900/50 hover:bg-slate-900 ${connected ? "opacity-60" : ""}`}
          >
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 bg-white rounded-lg flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-6 h-6">
                  <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                </svg>
              </div>
              <span className="text-indigo-500 font-medium">
                {connected ? "Gmail Connected" : "Connect Gmail"}
              </span>
            </div>
            {connected && <CheckCircle2 className="w-5 h-5 text-green-400" />}
            {loading && <RefreshCw className="w-5 h-5 text-indigo-400 animate-spin" />}
          </button>
        </div>
        
        <div className="text-xs text-slate-400 flex items-center gap-2">
          <Shield className="w-3 h-3" />
          Tokens are encrypted at rest with a server-side key.
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={onClose} className="border-slate-600 text-slate-400">
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Main CRM Settings Page
export default function CRMSettingsPage() {
  const [searchParams] = useSearchParams();
  const [gmailStatus, setGmailStatus] = useState({ connected: false, email: null });
  const [loading, setLoading] = useState(true);
  const [hasAccess, setHasAccess] = useState(true);
  const [connectOpen, setConnectOpen] = useState(false);
  const [confirmDisconnect, setConfirmDisconnect] = useState(false);

  useEffect(() => {
    loadData();
    
    // Handle OAuth callback params
    const success = searchParams.get("success");
    const error = searchParams.get("error");
    
    if (success === "google") {
      toast.success("Gmail connected successfully!");
      window.history.replaceState({}, "", "/crm/settings");
    } else if (error) {
      toast.error(`Connection failed: ${error}`);
      window.history.replaceState({}, "", "/crm/settings");
    }
  }, [searchParams]);

  const loadData = async () => {
    setLoading(true);
    try {
      const statusRes = await api.get("/crm/oauth/status");
      const gmail = statusRes.data.providers?.find(p => p.provider === "google");
      setGmailStatus({
        connected: gmail?.connected || false,
        email: gmail?.email || null,
        last_synced_at: gmail?.last_synced_at
      });
      setHasAccess(true);
    } catch (e) {
      if (e.response?.status === 403 && e.response?.data?.detail?.code === "FEATURE_GATED") {
        setHasAccess(false);
      } else {
        toast.error("Failed to load CRM settings");
      }
    } finally {
      setLoading(false);
    }
  };

  const disconnect = async () => {
    try {
      await api.delete("/crm/oauth/disconnect?provider=google");
      toast.success("Gmail disconnected");
      setGmailStatus({ connected: false, email: null });
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect");
    }
    setConfirmDisconnect(false);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-indigo-400 animate-spin" />
      </div>
    );
  }

  if (!hasAccess) {
    return (
      <div className="space-y-6" data-testid="crm-gated">
        <div className="flex items-center gap-3">
          <Settings className="w-7 h-7 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">CRM Settings</h1>
            <p className="text-indigo-400">Connect your email for CRM integration</p>
          </div>
        </div>
        
        <Card className="bg-slate-800/50 border-indigo-500/30">
          <CardContent className="py-12 text-center">
            <Lock className="w-16 h-16 text-indigo-400 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-indigo-500 mb-2">Growth Tier Feature</h2>
            <p className="text-indigo-400 mb-6 max-w-md mx-auto">
              CRM email integration is available on the Growth tier. Upgrade to connect your 
              Gmail for automatic email logging.
            </p>
            <Button 
              onClick={() => window.location.href = "/billing"}
              className="bg-gradient-to-r from-indigo-500 to-indigo-500 hover:from-indigo-600 hover:to-indigo-600"
            >
              Upgrade to Growth
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="crm-settings-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <Settings className="w-7 h-7 text-indigo-400" />
          <div>
            <h1 className="text-2xl font-bold text-indigo-500">CRM Settings</h1>
            <p className="text-indigo-400">Manage email connection for CRM</p>
          </div>
        </div>
        
        <Button 
          variant="outline" 
          size="sm"
          onClick={() => window.location.href = "/crm"}
          className="border-slate-600 text-slate-400"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to CRM
        </Button>
      </div>

      {/* Gmail Card */}
      <Card className={`bg-slate-800/50 border transition-all ${
        gmailStatus.connected ? "border-green-500/30" : "border-slate-700/50"
      }`}>
        <CardContent className="p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              {/* Gmail Icon */}
              <div className="w-14 h-14 bg-white rounded-xl flex items-center justify-center">
                <svg viewBox="0 0 24 24" className="w-8 h-8">
                  <path fill="#EA4335" d="M24 5.457v13.909c0 .904-.732 1.636-1.636 1.636h-3.819V11.73L12 16.64l-6.545-4.91v9.273H1.636A1.636 1.636 0 0 1 0 19.366V5.457c0-2.023 2.309-3.178 3.927-1.964L5.455 4.64 12 9.548l6.545-4.91 1.528-1.145C21.69 2.28 24 3.434 24 5.457z"/>
                </svg>
              </div>
              
              <div>
                <div className="flex items-center gap-2">
                  <span className="font-semibold text-indigo-500 text-lg">Gmail</span>
                  {gmailStatus.connected && (
                    <Badge className="bg-green-500/20 text-green-400 text-xs">Connected</Badge>
                  )}
                </div>
                
                <p className="text-sm text-indigo-400">
                  {gmailStatus.connected ? gmailStatus.email : "Not connected"}
                </p>
                
                {gmailStatus.connected && gmailStatus.last_synced_at && (
                  <p className="text-xs text-slate-400 mt-1">
                    Last sync: {new Date(gmailStatus.last_synced_at).toLocaleString()}
                  </p>
                )}
              </div>
            </div>
            
            <div className="flex items-center gap-2">
              {!gmailStatus.connected ? (
                <Button 
                  onClick={() => setConnectOpen(true)}
                  className="bg-indigo-500 hover:bg-indigo-600"
                >
                  <Link2 className="w-4 h-4 mr-2" />
                  Connect Gmail
                </Button>
              ) : (
                <Button 
                  variant="outline"
                  onClick={() => setConfirmDisconnect(true)}
                  className="border-slate-600 text-slate-400"
                >
                  <Unlink className="w-4 h-4 mr-2" />
                  Disconnect
                </Button>
              )}
            </div>
          </div>
          
          {/* Info */}
          <div className="mt-4 p-3 bg-slate-900/50 rounded-lg border border-slate-700/50">
            <p className="text-sm text-indigo-400">
              <strong className="text-slate-400">What we access:</strong> Email headers only (sender, recipient, subject, date). 
              We don't read email content.
            </p>
            <p className="text-xs text-slate-400 mt-2 flex items-center gap-1">
              <Shield className="w-3 h-3" />
              Tokens are encrypted at rest.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* How it works */}
      <Card className="bg-slate-800/30 border-slate-700/30">
        <CardHeader>
          <CardTitle className="text-indigo-500 text-lg">How Email Sync Works</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-teal-700 text-xs font-bold">1</span>
            </div>
            <p className="text-sm text-indigo-400">
              Connect your Gmail account with read-only access.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-teal-700 text-xs font-bold">2</span>
            </div>
            <p className="text-sm text-indigo-400">
              We scan for emails from contacts already in your CRM.
            </p>
          </div>
          <div className="flex items-start gap-3">
            <div className="w-6 h-6 bg-teal-500/20 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
              <span className="text-teal-700 text-xs font-bold">3</span>
            </div>
            <p className="text-sm text-indigo-400">
              Emails appear in your deal's activity timeline automatically.
            </p>
          </div>
        </CardContent>
      </Card>

      {/* Email Connect Modal */}
      <EmailConnectModal
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onSuccess={() => {
          setConnectOpen(false);
          loadData();
        }}
      />

      {/* Disconnect Confirmation Dialog */}
      <Dialog open={confirmDisconnect} onOpenChange={setConfirmDisconnect}>
        <DialogContent className="bg-slate-800 border-slate-700">
          <DialogHeader>
            <DialogTitle className="text-indigo-500">Disconnect Gmail?</DialogTitle>
            <DialogDescription className="text-indigo-400">
              This will remove stored tokens and stop email syncing.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setConfirmDisconnect(false)}
              className="border-slate-600 text-slate-400"
            >
              Cancel
            </Button>
            <Button 
              onClick={disconnect}
              className="bg-red-500 hover:bg-red-600"
            >
              Disconnect
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
